'use client';

import renderAttributeValue from '@app/_components/VariantSelector/renderAttributeValue';
import Swatch from '@app/_components/VariantSelector/Swatch';
import { useMarket } from '@app/_context';
import showPrice from '@components/VariantSelector/showPrice';
import { Listbox } from '@headlessui/react';
import { ArrowDownIcon, ArrowUpIcon } from '@ngg/icons';
import type { Breadcrumb, Dictionary } from 'types';
import type { StockStatus as StockStatusType } from 'types/product';
import type { InitialVariantState } from 'types/variant';

import { FEATURE_FLAGS } from '@/config/featureFlags';
import { cn } from '@/lib/utils';
import useVariantSelector from '@/utils/hooks/useVariantSelector';
import {
    hasSoonBackInStockStatus,
    hasStockItemStatus,
    showStatus,
} from '@/utils/variant/variantUtils';

import { StockStatusIcon } from '../StockStatusIcon';

const selectableStockStatuses: StockStatusType[] = [
    'DeliveryItem',
    'StockItem',
    'OutOfStock',
    'SoldOut',
    'PendingSoldOut',
];

export type VariantSelectorProps = {
    initialVariantState: InitialVariantState;
    dictionary: Dictionary;
    className?: string;
    breadcrumbs?: Breadcrumb[];
};

/**
 * @todo Refactor using shadcn/ui and the Select component from Radux UI.
 * It's also too much logic and nesting in this component with no clear separation of concerns.
 */
const VariantSelector = ({
    initialVariantState,
    dictionary,
    className,
    breadcrumbs,
}: VariantSelectorProps) => {
    const { attributes, state, optionSortingToUse, onChange } =
        useVariantSelector({
            initialVariantState,
            breadcrumbs,
            dictionary,
        });

    const { locale } = useMarket().state.market;

    if (!attributes.length) {
        return null;
    }

    return (
        <div
            className={cn('relative flex flex-col gap-4', className)}
            data-testid="ProductVariantSelect">
            {attributes.map(({ key, Label }) => {
                const attribute = state.options.get(key);

                if (!attribute) return null;

                const options = [...attribute].sort(optionSortingToUse);

                return (
                    <Listbox
                        data-testid="ProductVariantList"
                        key={key}
                        as="div"
                        className="relative"
                        disabled={![...attribute].length}
                        value={state.selection[key] ?? null}
                        name={key}
                        onChange={(value) => onChange(key, value, attribute)}>
                        {({ open }) => (
                            <>
                                <Listbox.Label className="relative mb-2 block text-xs">
                                    {Label}
                                </Listbox.Label>
                                <Listbox.Button
                                    name={key}
                                    className={cn(
                                        'flex h-10 w-full items-center border border-black py-0 pr-4 text-left text-sm transition ui-disabled:border-grey-150 lg:h-12',
                                        !state.selection[key] &&
                                            'text-grey-300',
                                    )}>
                                    <div className="mr-auto flex items-center self-stretch truncate">
                                        {state.selection[key] ? (
                                            renderAttributeValue(
                                                state.selection[key],
                                                attribute,
                                            )
                                        ) : (
                                            <span className="ml-4">
                                                {dictionary?.chooseAttribute?.replace(
                                                    '%',
                                                    Label.toLowerCase(),
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    {open ? (
                                        <ArrowUpIcon className="text-base text-black" />
                                    ) : (
                                        <ArrowDownIcon className="text-base text-black" />
                                    )}
                                </Listbox.Button>
                                <Listbox.Options className="absolute left-0 right-0 z-50 max-h-96 overflow-y-auto overflow-x-hidden border border-t-0 border-black bg-body hr-y">
                                    {options.map(
                                        ([value, variantAttributes]) => {
                                            const stockItemStatus =
                                                hasStockItemStatus(
                                                    variantAttributes,
                                                );
                                            const soonBackInStock =
                                                !!hasSoonBackInStockStatus(
                                                    variantAttributes,
                                                );
                                            const stockStatusText = showStatus(
                                                variantAttributes,
                                                dictionary,
                                            );

                                            return (
                                                <Listbox.Option
                                                    key={value}
                                                    value={value}
                                                    disabled={
                                                        !variantAttributes.some(
                                                            (
                                                                variantAttribute,
                                                            ) =>
                                                                selectableStockStatuses.includes(
                                                                    variantAttribute.stockStatus,
                                                                ),
                                                        )
                                                    }
                                                    className="flex h-12 cursor-pointer items-center px-3 py-2.5 text-xs ui-active:bg-grey-100 ui-disabled:pointer-events-none ui-disabled:text-black/40 md:text-base">
                                                    {variantAttributes[0]
                                                        .swatch && (
                                                        <Swatch
                                                            disabled={
                                                                !FEATURE_FLAGS
                                                                    .products
                                                                    .swatches
                                                            }
                                                            name={
                                                                variantAttributes[0]
                                                                    .code
                                                            }
                                                            imgUrl={
                                                                variantAttributes[0]
                                                                    .swatch
                                                            }
                                                        />
                                                    )}
                                                    <span className="mr-auto truncate text-sm">
                                                        {
                                                            variantAttributes[0]
                                                                .label
                                                        }
                                                    </span>
                                                    <span className="ml-6 flex-1 whitespace-nowrap text-right text-xxs uppercase space-y-1">
                                                        <span className="ml-auto flex items-center justify-end gap-x-1">
                                                            {(stockItemStatus ||
                                                                soonBackInStock) && (
                                                                <StockStatusIcon />
                                                            )}
                                                            {stockStatusText}
                                                        </span>
                                                    </span>
                                                    <div className="ml-6 flex-shrink-0 text-right text-sm space-y-px">
                                                        {showPrice({
                                                            variantAttributes,
                                                            locale,
                                                            dictionary,
                                                        })}
                                                    </div>
                                                </Listbox.Option>
                                            );
                                        },
                                    )}
                                </Listbox.Options>
                            </>
                        )}
                    </Listbox>
                );
            })}
        </div>
    );
};

VariantSelector.displayName = 'VariantSelector';

export default VariantSelector;
